"use client";
import type { ReactNode } from "react";
import {
  Activity,
  ArrowUpRight,
  Check,
  Monitor,
  Smartphone,
  Zap,
} from "lucide-react";
import { Badge } from "../../design-system";
import "../admin/admin.css";
import "./meta-crew.css";

export const jobs = [
  {
    title: "가을 진료 안내 블로그 발행",
    phone: "블로그 01",
    state: "진행 중",
    tone: "blue",
    step: "사진 첨부 후 본문 확인 중",
    time: "02:38",
    progress: 62,
  },
  {
    title: "신규 장비 소개 영상 게시",
    phone: "블로그 02",
    state: "진행 중",
    tone: "blue",
    step: "영상 업로드 완료 대기",
    time: "04:12",
    progress: 38,
  },
  {
    title: "진료 시간 안내 발행",
    phone: "블로그 01",
    state: "실패",
    tone: "coral",
    step: "네이버 로그인이 필요합니다",
    time: "01:21",
    progress: 0,
  },
  {
    title: "주말 건강관리 안내 발행",
    phone: "블로그 02",
    state: "성공",
    tone: "teal",
    step: "게시 URL과 완료 화면 저장",
    time: "03:42",
    progress: 100,
  },
];
export function Brand({ bridge = false }: { bridge?: boolean }) {
  return (
    <span className="meta-crew-brand">
      <span>
        <Activity size={19} />
      </span>
      Meta Crew <small>{bridge ? "Worker" : "Console"}</small>
    </span>
  );
}
export function Panel({
  title,
  extra,
  children,
  className = "",
}: {
  title: string;
  extra?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`meta-crew-panel ${className}`}>
      <header>
        <h2>{title}</h2>
        {extra}
      </header>
      {children}
    </section>
  );
}
export function Metrics({ bridge = false }: { bridge?: boolean }) {
  return (
    <dl className="admin-metrics meta-crew-metrics">
      {(bridge
        ? [
            ["진행 중", "2", "동시 실행 2개"],
            ["대기", "4", "다음 예약 14:30"],
            ["오늘 성공", "18", "중앙 집계"],
            ["오늘 실패", "1", "로그인 확인 필요"],
          ]
        : [
            ["온라인 머신", "12 / 13", "1대 현장 확인 필요"],
            ["사용 가능 디바이스", "28 / 30", "2대 연결 확인"],
            ["진행 중 작업", "8", "대기 12건"],
            ["오늘 완료", "146", "성공 142 · 실패 4"],
          ]
      ).map(([label, value, note]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
          <small>{note}</small>
        </div>
      ))}
    </dl>
  );
}
export function StatusStrip({
  offline = false,
  notReady = false,
  limited = false,
}: {
  offline?: boolean;
  notReady?: boolean;
  limited?: boolean;
}) {
  return (
    <div className="meta-crew-status-strip">
      {[
        [Monitor, "서버", offline ? "다시 연결 중" : "연결됨"],
        [Activity, "프로그램", "정상 · v1.2.0"],
        [
          Zap,
          "AI",
          notReady
            ? "로그인 필요"
            : limited
              ? "한도 도달 · 100%"
              : "로그인됨 · 62%",
        ],
        [
          Smartphone,
          "디바이스",
          notReady ? "연결 필요" : "2대 준비 · 1대 확인",
        ],
      ].map(([Icon, title, detail]) => {
        const I = Icon as typeof Monitor;
        return (
          <div key={String(title)}>
            <I size={16} />
            <span>
              <strong>{String(title)}</strong>
              <small>{String(detail)}</small>
            </span>
            <i
              className={
                title === "디바이스" ||
                (title === "AI" && (notReady || limited)) ||
                (title === "서버" && offline)
                  ? "warn"
                  : ""
              }
            />
          </div>
        );
      })}
    </div>
  );
}
export function PhonePreview() {
  return (
    <div className="meta-crew-phone-preview" aria-label="디바이스 화면 미리보기 예시">
      <div className="meta-crew-phone-top">
        14:12 <span>LTE · 86%</span>
      </div>
      <div className="meta-crew-phone-app">
        <b>N</b> 블로그 <span>발행</span>
      </div>
      <div className="meta-crew-phone-cover">
        <span>온유의원</span>
        <strong>
          가을을 건강하게
          <br />
          보내는 방법
        </strong>
        <div className="meta-crew-cover-orbit" />
      </div>
      <strong>계절이 바뀔 때, 건강도 챙기세요</strong>
      <p>
        일상 속 작은 습관으로 시작하는
        <br />
        가을 건강관리 안내.
      </p>
      <div className="meta-crew-text-line" />
      <div className="meta-crew-text-line short" />
      <footer>
        <Check size={12} /> 사진 3장 첨부 완료
      </footer>
    </div>
  );
}
export function Timeline() {
  return (
    <ol className="meta-crew-timeline">
      {[
        ["14:12:38", "본문과 첨부 사진을 확인하고 있습니다", "진행 중"],
        ["14:12:16", "사진 3장을 첨부했습니다", "완료"],
        ["14:11:42", "제목과 본문을 입력했습니다", "완료"],
        ["14:10:00", "파일 전달과 미디어 준비를 마쳤습니다", "완료"],
      ].map(([time, text, state]) => (
        <li key={time}>
          <span className="meta-crew-dot" />
          <time>{time}</time>
          <span>{text}</span>
          <small>{state}</small>
        </li>
      ))}
    </ol>
  );
}
export function Queue() {
  return (
    <div className="meta-crew-queue">
      {[
        ["01", "오후 진료 안내 발행", "블로그 01 · 앞 작업 완료 후"],
        ["02", "블로그 통계 화면 읽기", "블로그 02 · 14:30 예약"],
        ["03", "주간 건강 정보 발행", "블로그 01 · 15:00 예약"],
      ].map(([n, title, sub]) => (
        <div key={n}>
          <span>{n}</span>
          <section>
            <strong>{title}</strong>
            <small>{sub}</small>
          </section>
          <Badge>대기</Badge>
        </div>
      ))}
    </div>
  );
}
export function History() {
  return (
    <div className="meta-crew-history">
      {jobs.slice(2).map((j) => (
        <div key={j.title}>
          <Badge tone={j.tone}>{j.state}</Badge>
          <section>
            <strong>{j.title}</strong>
            <small>{j.step}</small>
          </section>
          <span>{j.time}</span>
        </div>
      ))}
    </div>
  );
}
export function ActivityChart() {
  return (
    <div
      className="meta-crew-chart"
      role="img"
      aria-label="중앙에서 제공한 24시간 작업 결과 예시: 성공 18건, 실패 1건"
    >
      <div className="meta-crew-bars">
        {[
          12, 8, 5, 5, 5, 6, 18, 32, 46, 70, 42, 88, 60, 78, 32, 18, 8, 5, 5, 5,
          5, 5, 5, 5,
        ].map((v, i) => (
          <span
            key={i}
            style={{ height: `${v}%` }}
            className={i === 12 ? "failure" : ""}
          />
        ))}
      </div>
      <div className="meta-crew-axis">
        <span>00:00</span>
        <span>06:00</span>
        <span>12:00</span>
        <span>18:00</span>
        <span>24:00</span>
      </div>
    </div>
  );
}
export function SampleNote() {
  return (
    <span className="meta-crew-sample">
      <ArrowUpRight size={12} /> 디자인 검토용 · 예시 데이터
    </span>
  );
}
